/** TanStack Query hooks for admin step management. */
import { keepPreviousData, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { toast } from 'sonner';
import { extractErrorMessage } from '@/shared/utils/extractErrorMessage';
import type { Step, PaginatedResponse } from '@formency/shared';
import type { AdminStepListItem, StepStats } from '@/shared/types/steps';

export const adminStepKeys = {
  all: ['adminSteps'] as const,
  list: (params?: Record<string, string>) => ['adminSteps', params] as const,
  stats: (stepId: string) => ['stepStats', stepId] as const,
  detail: (stepId: string) => ['step', stepId] as const,
};

/** Paginated admin steps list with hierarchy, stats, and tags. */
export function useAdminSteps(params?: Record<string, string>) {
  return useQuery({
    queryKey: adminStepKeys.list(params),
    queryFn: () =>
      api.get<PaginatedResponse<AdminStepListItem>>(
        '/steps/admin',
        params as Record<string, unknown>
      ),
    staleTime: 30 * 1000,
    placeholderData: keepPreviousData,
  });
}

/** Fetch a single step by ID. */
export function useStep(stepId: string | null) {
  return useQuery({
    queryKey: adminStepKeys.detail(stepId!),
    queryFn: () => api.get<Step>(`/steps/${stepId}`),
    enabled: !!stepId,
    staleTime: 30 * 1000,
  });
}

/** Performance stats for a single step. */
export function useStepStats(stepId: string | null) {
  return useQuery({
    queryKey: adminStepKeys.stats(stepId!),
    queryFn: () => api.get<StepStats>(`/steps/${stepId}/stats`),
    enabled: !!stepId,
    staleTime: 60 * 1000,
    placeholderData: keepPreviousData,
  });
}

/** Standalone step update (PUT /steps/:stepId). Invalidates admin steps + detail queries. */
export function useUpdateStepStandalone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ stepId, ...data }: { stepId: string; [key: string]: unknown }) =>
      api.put<Step>(`/steps/${stepId}`, data),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: adminStepKeys.all });
      qc.invalidateQueries({ queryKey: adminStepKeys.detail(variables.stepId) });
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error, 'Failed to update step'));
    },
  });
}
